<template>
    <dialog v-show="isOpen" ref="dialogElement" class="thought-process-dialog" @cancel="close">
        <div class="overflow-hidden grow flex flex-col gap-6 md:gap-10 items-center">
            <div class="overflow-hidden flex flex-col gap-6 items-center">
                <div class="shrink-0">
                    <CoachingModeMarble is-loading size="big" />
                </div>

                <h2 class="shrink-0 text-valence-grey-850 display-serif text-center w-52 mx-auto">A peek into my thought process</h2>
                <div v-if="hasError" class="shrink-0 body-semibold text-valence-grey-750">Sorry, I'm having trouble providing an explanation. Please try again later.</div>
                <div v-else-if="isLoading" class="shrink-0 flex justify-center">
                    <LoadingSpinner class="h-8 text-[#2C2C2C]" />
                </div>
                <!-- eslint-disable-next-line vue/no-v-html -- Content is sanitized with DOMPurify before rendering (see computed explanation) -->
                <div v-else-if="!!explanation" class="grow overflow-y-auto flex flex-col gap-4 *:whitespace-pre-line text-valence-grey-750" v-html="explanation"></div>
            </div>
            <div class="shrink-0 inline-block mx-auto">
                <button
                    type="button"
                    class="transition-colors rounded-3xl px-9 py-3 leading-normal body-bold w-40 ring-2 ring-inset bg-valence-primary-500 hover:bg-[#4B508F] hover:ring-[#4B508F] text-white ring-valence-primary-500"
                    @click="close">
                    Dismiss
                </button>
            </div>
        </div>
    </dialog>
</template>

<script setup>
import { ACTION, lineIsAction } from "~vue/chatActions";
import CoachingModeMarble from "~vue/components/navigation/CoachingModeMarble.vue";
import { CHAT_EVENT } from "~vue/events";
import LoadingSpinner from "~vue/icons/LoadingSpinner.vue";
import { marked } from "marked";
import { computed, inject, onMounted, onUnmounted, ref, useTemplateRef, watch } from "vue";

import { noImageRenderer, sanitizeForChat } from "./utils/markdown";

const props = defineProps({
    messages: { type: Array, default: () => [] },
});

const { emitter, $sendEvent } = inject("globalProperties");
const sendStreamingEvent = inject("sendStreamingEvent");

const messageId = ref(null);
const isLoading = ref(false);
const hasError = ref(false);
const dialogElement = useTemplateRef("dialogElement");
const isOpen = ref(false);

watch(isOpen, (value) => {
    if (value) {
        dialogElement.value.showModal();
    } else {
        dialogElement.value.close();
    }
});

function open(msgId) {
    messageId.value = msgId;
    isOpen.value = true;
}

function close() {
    messageId.value = null;
    isOpen.value = false;
}

onMounted(() => {
    emitter.on(CHAT_EVENT.OPEN_THOUGHT_PROCESS_DIALOG, open);
});

onUnmounted(() => {
    emitter.off(CHAT_EVENT.OPEN_THOUGHT_PROCESS_DIALOG, open);
});

const message = computed(() => {
    if (!messageId.value) {
        return null;
    }
    const result = props.messages.find((m) => m.chat_message_id === messageId.value);
    return result;
});

const explanation = computed(() => {
    if (!message.value) {
        return null;
    }
    const explanationLine = message.value.lines.find((line) => lineIsAction(line) && line.action_name === ACTION.LLM_EXPLANATION);
    if (!explanationLine) {
        return null;
    }
    const content = explanationLine.action_params?.content ?? "";
    return sanitizeForChat(marked.parse(content, { extensions: { renderers: noImageRenderer } }));
});

watch(
    message,
    (newMessage) => {
        if (!newMessage) {
            return;
        }
        const explanationLine = newMessage.lines.find((line) => lineIsAction(line) && line.action_name === ACTION.LLM_EXPLANATION);
        if (explanationLine) {
            isLoading.value = false;
        } else {
            hasError.value = false;
            isLoading.value = true;
            const payload = {
                chat_message_id: newMessage.chat_message_id,
            };
            const send = sendStreamingEvent ? sendStreamingEvent("explain_llm_response", payload) : $sendEvent("explain_llm_response", payload);
            send.catch((error) => {
                console.error("Failed to send explain_llm_response event", error);
                hasError.value = true;
            });
        }
    },
    { deep: true },
);
</script>

<style scoped>
@reference "../../css/tailwind.css";
.thought-process-dialog {
    @apply top-auto bottom-0 md:top-0 rounded-tl-3xl md:max-h-[75vh] rounded-tr-3xl md:rounded-3xl bg-white p-6 md:p-10 mx-auto w-full max-w-full md:max-w-(--breakpoint-md) w-full flex flex-col items-center;
    box-shadow: 0px 4px 24px rgba(0, 0, 0, 0.12);
}

.thought-process-dialog::backdrop {
    background: rgba(23, 55, 100, 0.4);
    backdrop-filter: blur(4px);
}
</style>
